import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Tag } from "primereact/tag";
import { useAppContext } from "../../providers/ProviderAppContainer";
import PageTitle from "../common/PageTitle";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import { getWriteableDate, toAppDate } from "../../utils";
import { EMPTY_VALUE } from "../../constants";
import { TEXT_NORMAL, TEXT_SMALL, TEXT_SUBTITLE } from "../../style";
import DialogCalendarEvent from "./DialogCalendarEvent";
import ChoghadiyaPanel from "./ChoghadiyaPanel";

function parseDateParam(value) {
    if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const parsed = toAppDate(value);
    if (!parsed || Number.isNaN(parsed.getTime())) return null;
    parsed.setHours(0, 0, 0, 0);
    return parsed;
}

function todayAtMidnight() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
}

const DAY_START_HOUR = 0;
const DAY_HOURS = 24;
const HOUR_HEIGHT = 56;
const TIME_GUTTER = "3.25rem";
const EVENT_GAP = "0.25rem";
const HOURS = Array.from({ length: DAY_HOURS }, (_, i) => DAY_START_HOUR + i);
const TIMELINE_HEIGHT = DAY_HOURS * HOUR_HEIGHT;
const DAY_MINUTES = DAY_HOURS * 60;

function parseEventDate(value) {
    return toAppDate(value) || (value ? new Date(value) : null);
}

function minutesFromDayStart(date) {
    if (!date) return 0;
    return date.getHours() * 60 + date.getMinutes() - DAY_START_HOUR * 60;
}

function eventDurationMinutes(start, end) {
    const ms = Math.max(15 * 60 * 1000, (end?.getTime?.() || 0) - (start?.getTime?.() || 0));
    return ms / (60 * 1000);
}

function packOverlaps(events) {
    const items = events.map((event, index) => {
        const start = parseEventDate(event.start_at);
        const end = parseEventDate(event.end_at);
        return {
            event,
            index,
            startMin: minutesFromDayStart(start),
            endMin: minutesFromDayStart(start) + eventDurationMinutes(start, end),
            column: 0,
            columns: 1,
        };
    });

    items.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin || a.index - b.index);

    const clusters = [];
    let cluster = [];
    let clusterEnd = -Infinity;

    for (const item of items) {
        if (!cluster.length || item.startMin < clusterEnd) {
            cluster.push(item);
            clusterEnd = Math.max(clusterEnd, item.endMin);
        } else {
            clusters.push(cluster);
            cluster = [item];
            clusterEnd = item.endMin;
        }
    }
    if (cluster.length) clusters.push(cluster);

    for (const group of clusters) {
        const colEnds = [];
        for (const item of group) {
            let column = 0;
            while (column < colEnds.length && colEnds[column] > item.startMin) column += 1;
            if (column === colEnds.length) colEnds.push(item.endMin);
            else colEnds[column] = item.endMin;
            item.column = column;
        }
        const columns = Math.max(1, colEnds.length);
        group.forEach((item) => {
            item.columns = columns;
        });
    }

    return items;
}

function typeSeverity(type) {
    if (type === "BATCH_SCHEDULE") return "info";
    if (type === "BATCH_EVENT") return "warning";
    return "success";
}

function typeLabel(type) {
    if (type === "BATCH_SCHEDULE") return "Schedule";
    if (type === "BATCH_EVENT") return "Event";
    return "Personal";
}

function shiftDate(date, days) {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
}

export default function CalendarDay() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { requestAPI, showToast } = useAppContext();
    const [selectedDate, setSelectedDate] = useState(() => parseDateParam(searchParams.get("date")) || todayAtMidnight());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [dialogEvent, setDialogEvent] = useState({ visible: false });
    const [choghadiya, setChoghadiya] = useState(null);
    const [choghadiyaLoading, setChoghadiyaLoading] = useState(true);
    const [choghadiyaError, setChoghadiyaError] = useState();
    const [holiday, setHoliday] = useState(null);

    const dateKey = useMemo(() => getWriteableDate({ date: selectedDate, removeTime: true }), [selectedDate]);

    useEffect(() => {
        const fromQuery = parseDateParam(searchParams.get("date"));
        if (!fromQuery) return;
        setSelectedDate((prev) => (prev.getTime() === fromQuery.getTime() ? prev : fromQuery));
    }, [searchParams]);

    useEffect(() => {
        if (!dateKey || dateKey === EMPTY_VALUE) return;
        if (searchParams.get("date") === dateKey) return;
        setSearchParams({ date: dateKey }, { replace: true });
    }, [dateKey, searchParams, setSearchParams]);

    const loadEvents = useCallback(() => {
        if (!dateKey || dateKey === EMPTY_VALUE) return;

        requestAPI({
            requestPath: "calendar",
            requestMethod: "GET",
            requestGetQuery: { date: dateKey },
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (Array.isArray(data) && responseCode === 200) {
                    setEvents(data);
                    setError();
                } else {
                    setEvents([]);
                    setError(data?.error || "Couldn't load calendar");
                }
            },
        });
    }, [dateKey, requestAPI]);

    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    useEffect(() => {
        if (!dateKey || dateKey === EMPTY_VALUE) return;

        setChoghadiya(null);
        setChoghadiyaError();
        setHoliday(null);

        requestAPI({
            requestPath: "calendar/choghadiya",
            requestMethod: "GET",
            requestGetQuery: { date: dateKey },
            setLoading: setChoghadiyaLoading,
            onRequestFailure: setChoghadiyaError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200 && data.date === dateKey) {
                    setChoghadiya(data);
                    setChoghadiyaError();
                } else {
                    setChoghadiya(null);
                    setChoghadiyaError(data?.error || "Couldn't load choghadiya");
                }
            },
        });

        requestAPI({
            requestPath: "calendar/holidays",
            requestMethod: "GET",
            requestGetQuery: { from: dateKey, to: dateKey },
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setHoliday(data[dateKey] || null);
                } else {
                    setHoliday(null);
                }
            },
        });
    }, [dateKey, requestAPI]);

    const packed = useMemo(() => packOverlaps(events), [events]);

    const closeDialog = useCallback(() => setDialogEvent((prev) => ({ ...prev, visible: false })), []);

    const openCreate = useCallback(() => {
        const start = new Date(selectedDate);
        start.setHours(9, 0, 0, 0);
        const end = new Date(selectedDate);
        end.setHours(10, 0, 0, 0);
        setDialogEvent({
            visible: true,
            mode: "create",
            title: "",
            description: "",
            start_at: start,
            end_at: end,
            closeDialog,
            onSaved: loadEvents,
        });
    }, [closeDialog, loadEvents, selectedDate]);

    const openEdit = useCallback(
        (event) => {
            if (!event.editable || event.type !== "USER_EVENT") return;
            setDialogEvent({
                visible: true,
                mode: "edit",
                id: event.source_id,
                title: event.title,
                description: event.description || "",
                start_at: parseEventDate(event.start_at),
                end_at: parseEventDate(event.end_at),
                closeDialog,
                onSaved: loadEvents,
            });
        },
        [closeDialog, loadEvents],
    );

    return (
        <div className="flex flex-column h-full overflow-hidden">
            <PageTitle title="Calendar" />
            <div className="flex-1 min-h-0 overflow-scroll">
                <div className="px-3 pt-3 flex align-items-center justify-content-between gap-2">
                    <Button icon="pi pi-chevron-left" rounded text onClick={() => setSelectedDate((prev) => shiftDate(prev, -1))} />
                    <Calendar
                        value={selectedDate}
                        onChange={(e) => e.value && setSelectedDate(e.value)}
                        dateFormat="dd/mm/yy"
                        showIcon
                        className="w-full"
                    />
                    <Button icon="pi pi-chevron-right" rounded text onClick={() => setSelectedDate((prev) => shiftDate(prev, 1))} />
                </div>

                <div className="px-3 py-2 flex align-items-center justify-content-between gap-2">
                    <span className={`${TEXT_SUBTITLE} font-semibold`}>
                        {selectedDate.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}
                    </span>
                    <Button label="Add" icon="pi pi-plus" size="small" severity="warning" onClick={openCreate} />
                </div>

                {holiday?.name ? (
                    <div className="px-3 pb-2">
                        <div className="flex align-items-center justify-content-between gap-2 border-round px-3 py-2 bg-red-50 border-1 border-red-200">
                            <span className={`${TEXT_NORMAL} font-semibold`}>{holiday.name}</span>
                            <Tag value="Holiday" severity="danger" pt={{ value: { className: TEXT_SMALL } }} />
                        </div>
                    </div>
                ) : null}

                <ChoghadiyaPanel loading={choghadiyaLoading} error={choghadiyaError} data={choghadiya} />

                <div className="px-3 pb-2 flex flex-wrap gap-2">
                    <Tag value="Schedule" severity="info" pt={{ value: { className: TEXT_SMALL } }} />
                    <Tag value="Batch event" severity="warning" pt={{ value: { className: TEXT_SMALL } }} />
                    <Tag value="Personal" severity="success" pt={{ value: { className: TEXT_SMALL } }} />
                    {holiday ? <Tag value="Holiday" severity="danger" pt={{ value: { className: TEXT_SMALL } }} /> : null}
                </div>

                <div className="px-2 pb-3">
                    {loading ? (
                        <Loading message="Loading calendar" />
                    ) : error ? (
                        <NoContent error={error} />
                    ) : (
                        <div className="relative border-1 border-gray-200 border-round" style={{ height: TIMELINE_HEIGHT }}>
                            {HOURS.map((hour, index) => (
                                <div
                                    key={hour}
                                    className="absolute left-0 right-0 border-top-1 border-gray-100 flex"
                                    style={{ top: index * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                                >
                                    <span className={`${TEXT_SMALL} text-color-secondary px-2`}>{`${String(hour).padStart(2, "0")}:00`}</span>
                                </div>
                            ))}

                            <div className="absolute top-0 bottom-0" style={{ left: TIME_GUTTER, right: EVENT_GAP }}>
                                {packed.map(({ event, startMin, endMin, column, columns }) => {
                                    const clampedStart = Math.max(0, Math.min(DAY_MINUTES - 15, startMin));
                                    const clampedEnd = Math.max(clampedStart + 15, Math.min(DAY_MINUTES, endMin));
                                    const top = (clampedStart / 60) * HOUR_HEIGHT;
                                    const height = Math.max(40, ((clampedEnd - clampedStart) / 60) * HOUR_HEIGHT);
                                    const cancelled = !!event.cancelled;
                                    const lanePct = 100 / Math.max(1, columns);

                                    return (
                                        <div
                                            key={event.id}
                                            role={event.editable ? "button" : undefined}
                                            className={`absolute border-round px-2 py-1 overflow-hidden ${
                                                cancelled ? "bg-gray-200 text-color-secondary" : "bg-primary-50 border-1 border-primary-200"
                                            }`}
                                            style={{
                                                top,
                                                height: Math.min(height, TIMELINE_HEIGHT - top),
                                                left: `${column * lanePct}%`,
                                                width: `${lanePct}%`,
                                                paddingLeft: 4,
                                                paddingRight: 4,
                                                boxSizing: "border-box",
                                                opacity: cancelled ? 0.7 : 1,
                                                cursor: event.editable ? "pointer" : "default",
                                            }}
                                            onClick={() => openEdit(event)}
                                        >
                                            <div className="flex align-items-center justify-content-between gap-1">
                                                <Tag value={typeLabel(event.type)} severity={typeSeverity(event.type)} pt={{ value: { className: TEXT_SMALL } }} />
                                                {cancelled && <Tag value="Cancelled" severity="danger" pt={{ value: { className: TEXT_SMALL } }} />}
                                            </div>
                                            <div className={`${TEXT_NORMAL} font-semibold ${cancelled ? "line-through" : ""}`}>{event.title}</div>
                                            {event.type === "BATCH_SCHEDULE" && event.batch_title ? (
                                                <div className={`${TEXT_SMALL} text-color-secondary`}>{event.batch_title}</div>
                                            ) : null}
                                            {event.type === "BATCH_SCHEDULE" && event.event_handler_name ? (
                                                <div className={`${TEXT_SMALL} text-color-secondary`}>{event.event_handler_name}</div>
                                            ) : null}
                                            <div className={TEXT_SMALL}>
                                                {parseEventDate(event.start_at)?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                                {" - "}
                                                {parseEventDate(event.end_at)?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {!events.length && (
                                <div className="absolute left-0 right-0 top-0 bottom-0 flex align-items-center justify-content-center">
                                    <NoContent error="No events for this day" />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {dialogEvent.visible && <DialogCalendarEvent {...dialogEvent} showToast={showToast} requestAPI={requestAPI} />}
        </div>
    );
}
